 
import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react";
import _ from "lodash";
import embLabels from "../config/emb-labels-en.json";
import ACTIONS from "./DesignAssets/Design_Asset_Actions.json";
import PROJECT_ACTION from "./DesignAssets/Actions/projectKind.json"
import { upDateProjectType } from "../redux/features/projectTypeSlice";

//* extracted hooks & utilities
import useDesignAssetState from "./DesignAssets/hooks/useDesignAssetState";
import {
  padNumberFormat,
  isDuplicates,
  pvSetGroupName,
  DER,
  priorityRules as prorityRules,
  applyPriorityRules,
  isBetween,
  isValidCBName,
  isValidName,
  checkForDuplicateName as checkForDuplicateNameUtil,
  getAllNames as getAllNamesUtil,
} from "./DesignAssets/hooks/designAssetUtils";

//* reducer import

import {
  initialCBAssetList,
  CBAssetListReducer,
} from "./DesignAssets/Reducers/CBAssetListReducer";
import {
  initialCBEVList,
  CBEVListReducer,
} from "./DesignAssets/Reducers/CBEVListReducer";
import {
  initialCBLoadList,
  CBLoadListReducer,
} from "./DesignAssets/CBLoadListReducer";
import {
  initialPVDetails,
  PVDetailsReducer,
} from "./DesignAssets/Reducers/PVDetailsReducer";
import { initialCBGTList, CBGTListReducer } from "./DesignAssets/Reducers/CBGTListReducer";
import {
  initialCount,
  countReducer,
} from "./DesignAssets/Reducers/CountReducer";

import {
  initialTypes,
  projectKindReducer,
} from './DesignAssets/Reducers/projectKindReducer'

//* custom hook

import useMountEffect from "./CustomHooks/useMountEffect";
import userScope from "./CustomHooks/userScope";

//* context api

import {
  DesignAssetProvider,
  AssetBlockContextProvider,
} from "./CustomHooks/ContextApi";
import Snackbar from "./SE-Component/Snackbar";
import AlertIcon from "./SE-Component/AlertIcon";
import Confirm from "./SE-Component/Confirm";

//* child import

import IslandableSLD from "./DesignAssets/IslandableSLD";
import GridConnectedRosette from "./DesignAssets/GridConnectedRosette";
import PVHybridizationSLD from "./DesignAssets/PVHybridizationSLD";
import PvGensetAtsSLD from "./DesignAssets/PvGensetAtsSLD";
import PvBessNrSLD from "./DesignAssets/PvBessNrSLD";
import A2ESLD from "./DesignAssets/A2ESLD";
import AddEquipment from "./DesignAssets/AddEquipment";
import DesignAssetDescription from "./DesignAssets/DesignAssetDescription";
import UtilityAsset from "./DesignAssets/UtilityAsset";
import GensetAsset from "./DesignAssets/GensetAsset";
import GensetAssetPvHyb from "./DesignAssets/GensetAssetPvHyb";
import BatteryAsset from "./DesignAssets/BatteryAsset";
import PVAsset from "./DesignAssets/PVAsset";
import CHPAsset from "./DesignAssets/CHPAsset";
import HybridAsset from "./DesignAssets/HybridAsset";
import CircuitBreakerDetails from "./DesignAssets/CircuitBreakerDetails";
import GsxNewSBCbDetails from "./DesignAssets/GsxNewSBCbDetails";
import CbPmDetailsA2E from "./DesignAssets/CbPmDetailsA2E";
import CbPmDetailsGrid from "./DesignAssets/CbPmDetailsGrid";
import LoadAsset from "./DesignAssets/LoadAsset";
import LoadCB from "./DesignAssets/LoadCB";
import EvCluster from "./DesignAssets/EvCluster";
import EvCB from "./DesignAssets/EvCB";
import NoAssetSelected from "./DesignAssets/NoAssetSelected";
import AssetSelectionDER from "../components/AssetSelectionDer";
import AssetSelectionPV from "../components/AssetSelectionPV";
import AddPvModals from "../components/AddPvModals";

//*api call
import ApiSession from "../api/ApiSession";
import GensetPvBessSLD from "./DesignAssets/GensetPvBessSLD";
import HardWiredSBCbDetails from "./DesignAssets/HardWiredSBCbDetails";
import { useDispatch, useSelector } from "react-redux";
import ExistingPmDetailsGrid from "./DesignAssets/ExistingPmDetailsGrid";
import MyErrorBoundary from "../utils/ErrorBoundary";
import BessHybridAsset from "./DesignAssets/BessHybridAsset";
import { coOrdinateValidator } from "../utils/coOrdinateValidator";
import { getTooltipData } from "../redux/features/getTooltipDataSlice";
import "./designAssethook.scss";
import { assignIpAddress } from "../utils/assignIPAddress";
import { generateRandom } from "../utils/randomGenerator";
import { getProject, getProjectId, getProjectTypeValue } from "../utils/project";
import GridEtoSLD from "./DesignAssets/GridEtoSLD";
import IslandableEtoSLD from "./DesignAssets/IslandableEtoSLD";
import { projectAtomList } from "../atoms";
import { useAtom } from "jotai";
import useProjectTypeValue from "../utils/useCurrentProjectValue";
import { ComponentDataReducer, initialComponentData } from "./DesignAssets/Reducers/ComponentDataReducer";
import GensetPvHybridEto from "./DesignAssets/GensetPvHybridEto";
import DraggableModal from "./utils/DraggableModal";
import LoadAssetDetails from "./DesignAssets/LoadAssetDetails";
import LoadConfigDetailsModal from "../components/LoadConfigDetailsModal";
import RemoteLoadCbDetails from "./DesignAssets/RemoteLoadCbDetails";
import { GridEmaEtoSLD } from "../components/NewEma/GridTied/GridEmaEtoSLD";
import EmaIslandableEtoSLD from "../components/NewEma/Islandable/EmaIslandableEtoSLD";
import { MGaaS_A2E, MGAAS_ANSI, MGAAS_ETO, MGaaS_IEC, NMGAAS_ETO } from "../utils/application.constants";
import OffGridSLD from "./DesignAssets/OffGridSLD";
import GtDetails from "./DesignAssets/GtDetails";
import GtConfigDetailsModal from "../components/GtConfigDetailsModal";
import SldContainer from "../components/SldContainer";


//* extracted custom hooks
import useFetchAssetData from "./DesignAssets/hooks/useFetchAssetData";
import useGenerateComponents from "./DesignAssets/hooks/useGenerateComponents";
import useAssetSelection from "./DesignAssets/hooks/useAssetSelection";
import useAssetConfigHandlers from "./DesignAssets/hooks/useAssetConfigHandlers";
import useAssetOperations from "./DesignAssets/hooks/useAssetOperations";
import useAssetEditStatus from "./DesignAssets/hooks/useAssetEditStatus";
import useAssetSwitchboardHandler from "./DesignAssets/hooks/useSwitchboardHandler";
const apiSession = new ApiSession();
const allowedCbCount = 29;
const allowedLoadCount = 23;
const alphanumericRegWithHyphen = /^[-_0-9a-zA-Z]+$/;
const DesignAsset = forwardRef((props, ref) => {
  //* All state is managed via the useDesignAssetState custom hook
  const state = useDesignAssetState(props);
  const {
    projectTypeValue,
    dispatch,
    UserScope,
    projectType,
    loading, setLoading,
    displayPage, setDisplayPage,
    sldLoadCompleted, setSldLoadCompleted,
    useCaseValidGenset, setUseCaseValidGenset,
    deleteOneDER, setDeleteOneDER,
    defaultConfigurationStatus, setDefaultConfigurationStatus,
    siteCreated, setSiteCreated,
    isOpen, setIsOpen,
    siteType, setSiteType,
    evceDiagramChangeRequired, setEvceDiagramChangeRequired,
    isAllowMultipleLoads, setIsAllowMultipleLoads,
    isExportOptimization, setIsExportOptimization,
    switchboardType, setSwitchboardType,
    clientType, setClientType,
    architectureType, setArchitectureType,
    nominalVoltage, setNominalVoltage,
    isSelectSpanishCode, setIsSelectSpanishCode,
    defaultPreFillData, setDefaultPreFillData,
    availableDer, setAvailableDer,
    useCases, setUseCases,
    isEvce, setIsEvce,
    prevPvGroupNames, setPrevPvGroupNames,
    batteryCommunicationData, setBatteryCommunicationData,
    evCommunicationData, setEvCommunicationData,
    powerMeter, setPowerMeter,
    communicationState, setCommunicationState,
    cbCommunicationValues, setCbCommunicationValues,
    detailsBlock, setDetailsBlock,
    selectedValues, setSelectedValues,
    configData, setConfigData,
    isLoadNameUnique, setIsLoadNameUnique,
    gsxExternalId, setGsxExternalId,
    isBessController, setIsBessController,
    assetDetailsDisplay, setAssetDetailsDisplay,
    assetInfo, setAssetInfo,
    bessControllerPrevValue, setBessControllerPrevValue,
    cbDer, setCbder,
    CBValues, setCBValues,
    defaultsMinMax, setDefaultsMinMax,
    coordinates, setCoordinates,
    rectangleWidth, setRectangleWidth,
    snackBar, setSnackBar,
    alert, setAlert,
    confirm, setConfirm,
    remoteConfirm, setRemoteConfirm,
    duplicateUpdate, setDuplicateUpdate,
    runCalculateCoordinates, setRunCalculateCoordinates,
    runGenerateComponentsBlock, setRunGenerateComponentsBlock,
    batteryRack, setBatteryRack,
    batterySystemA2EConfig, setBatterySystemA2EConfig,
    isOpenLoadConfig, setIsOpenLoadConfig,
    isOpenGtConfig, setIsOpenGtConfig,
    loadSide, setLoadSide,
    isAntiIslandingRelayEnable, setIsAntiIslandingRelayEnable,
    gridTransitionOnline, setGridTransitionOnline,
    defaultCbData, setDefaultCbData,
    defaultPmData, setDefaultPmData,
    defaultDummyCbData, setDefaultDummyCbData,
    gensetAssetData, setGensetAssetData,
    evContactorData, setEVContactorData,
    gTContractorData, setGTContractorData,
    defaultGTData, setGTDefaultData,
    evDefaultData, setEVDefaultData,
    evPMData, setEVPMData,
    jsonData, setJsonData,
    isContactorEnabled, setIsContactorEnabled,
    cbLoadData, setCBLoadData,
    currentProject, setCurrentProject,
    defaultComponentData, setDefaultComponentData,
    updateComponents, setUpdateComponents,
    cbCount, setCbCount,
    spanishCodeRealTime, setSpanishCodeRealTime,
    assetDefaultValues, setAssetDefaultValues,
    isAssetConfigChanged, setIsAssetConfigChanged,
    gtTempConfigData, setGtTempConfigData,
    projectStatusData,
    deploymentReady, setDeploymentReady,
    ComponentData, ComponentDataDispatch,
    CBAssetList, CBAssetListDispatch,
    CBEVList, CBEVListDispatch,
    CBLoadList, CBLoadListDispatch,
    CBGTList, CBGTListDispatch,
    firstTimeCall, setFirstTimecall,
    multipleLoads, setMultipleLoads,
    count, countDispatch,
    projectKind, projectKindDispatch,
    PVDetails, PVDetailsDispatch,
    selectedLoadIndx, setSelectedLoadIndx,
    projectId,
    initialRenderCoordinates,
    initialRenderPvConfigured,
    snackBarRef,
    isUpdatingComponent,
    remoteMain, setRemoteMain,
    considerSelected, setConsiderSelected,
    assetDetails, setAssetDetails,
  } = state;

  const {
    isMgaasAnsi,
    isDaasAnsi,
    isDaasEto,
    isMgaasIec,
    isDaasIec,
    isMgaasAnsiGrid,
    isDaasAnsiGrid,
    isDaasEtoGrid,
    isMgaasAnsiIsland,
    isDaasAnsiIsland,
    isDaasEtoIsland,
    isMgaasIecGrid,
    isDaasIecGrid,
    isMgaasIecIsland,
    isGridConnected,
    isGridConnectedGSX,
    isIslandable,
    isIslandableGSX,
    isA2E,
    isLineSide,
    isExistingSwitchBoard,
    isLoadSide,
    isNewSwitchBoard,
    isGreenStruxure,
    isGeneric,
    isGreenStruxureMicrogrid,
    isPVGensetATS,
    isFuelSaver,
    isPVBessNR,
    isPVHybrid,
    isPVGensetA2E,
    isGensetMainNoBess,
    isNoFuel,
    isETO,
    isMGaaSETOGrid,
    isMGaaSETOIsland,
    isMGaaSETO,
    isMGaaSETOGensetPvHybrid,

    isNMGaaSETOGrid,
    isNMGaaSETOIsland,
    isNMGaaSETO,
    isNMGaaSETOGensetPvHybrid,

    isMgaasAnsiOffGrid,
    isMgaasIecOffGrid,
    isMassEtoOffGrid
  } = projectKind;


  //* Cross-hook function reference
  const fnRef = useRef({});

  //* Build deps for extracted hooks
  const deps = { ...state, ...projectKind, fnRef, allowedCbCount,allowedLoadCount };

  const { loadToolTip, getProjectDetailStatus, getExternalID, upDateCbcount, fetchDefaultDataValues, checkUseCaseChangeByUser, checkGensetDeleteOneDER, checkUseCase } = useFetchAssetData(deps);
  const { calculateCoOridinates, checkAssetStatus, checkExportOptimizationCondition, saveDraftComponentData, generateComponentsBlock, updateComponentRemote, getCBDetails, assignComponents } = useGenerateComponents(deps);
  const { setAssetSelection, getAssets, selectManufacturer, closeAssetSelectionBlock, selectAssetFromList, assetDetailsReset, selectedSubPvHandler, selectedAssetHandler, selectedAssetHandlerPv, loadSelectHandler, gtSelectHandler, selectedValuesHandler } = useAssetSelection(deps);
  const { assetConfigDataHandler, loadConfigDataHandler, gtConfigDataHandler, evConfigDataHandler, cbConfigDataHandler, newSBCbConfigDataHandler, loadCbConfigDataHandler, gtCbConfigDataHandler, evCbConfigDataHandler, pmConfigDataHandler, existingPmConfigDataHandler, loadpmConfigDataHandler, evpmConfigDataHandler, handleUtilityChange, handleGensetChange, handleBatteryChange, handleChpChange, handleHybridChange, handlePvChange, handleLoadChange, handleGtChange, handleEvChange, handleCBConfigChange, handleCBContactorConfigChange, handleCBContactorConfigChangeLoad, handleCBConfigChangeLoad, handleGtCbConfigChange, handleCBConfigChangeEv, calculateMaxAvailableUtilityPower, calculateMaxAvailableLoadPower, calculateMaxAvailableGensetPower, updateUtilitytConfig, updateGensetConfig, updateBatteryConfig, updateChpConfig, updateHybridConfig, arrayMaxPowerFinder, updatePvArrayConfig, updatePvConfig, updateLoadConfig, updateEvConfig, updateGtConfig, updateCbConfigData, updateCbContactorConfigData, updateEvCbConfigData, updateLoadCbConfigData, updateRemoteLoadCbConfigData, updatePowerMeterData, updateLoadCbPm, updateGtCb, pv1SelectHandler, pv2SelectHandler, pv3SelectHandler } = useAssetConfigHandlers(deps);
  const { getAllNames, checkForDuplicateName, addGensetVerify, addGenset, addBattery, addPv, addPVSetWithCB, addCHPVerify, addCHP, addLoad, addRemoteLoad, addEv, addGroundingTransformer, addTransferSwitch, checkGensetRemoveCondition, removeGenset, checkBatteryRemoveCondition, removeBattery, removePvWithAlert, checkPVCount, removePv, removeCHP, checkRemoteLoads, removeLoad, removeEv, removeGt, isNoAssetSelected, checkSLDCount } = useAssetOperations(deps);
  const { closePvDetailsBlock, isCopyAllButtonDisabled, copyAllPvs, assetEditStatusUpdateDER, assetEditStatusUpdateLoad, assetEditStatusUpdateAssetCB, assetEditStatusUpdateLoadCb, assetEditStatusUpdateGtCb, assetEditStatusUpdateEvCb, assetEditStatusUpdatePv, assetEditStatusUpdateAssetPM, assetEditStatusUpdateEvPM, checkUpdatedStatus, detailsModifiedStataus, updateDerHistory, updateLoadHistory, updateEvCbHistory, updateLoadCbHistory, updateGtCbHistory, updateAssetCbHistory, updateAssetPmHistory, updateEvPmHistory, checkCBStatus, checkPmStatus, updatePvHistoryFull, updatePvHistory, updateDerStatus, updatePvHistoryGroupPower } = useAssetEditStatus(deps);
  const { handleSwitchboardTypeChangeGSX, handleSetLoadSide, onAddPvExisiting, onAddBatteryExisting, removeExistingPm, handleEnergyReserve, handleCbAssetRequired, handleLoadCBAssetRequired, handleEVCbChange, handlePmAssetRequired, handleBessController, handleLoadPmAssetRequired, handleEmAssetRequired, handleRemoteMain } = useAssetSwitchboardHandler(deps);

  useEffect(() => {
    listAssets();
  }, [])

  const listAssets = async () => {
    try {
      const response = await apiSession.getAssets();
      if (response) {
        setAssetDetails(prevState => ({
          ...prevState,
          assets: response,
          assetsData: response,
        }));
      }
    } catch (error) {
      console.log(error)
    }
  };

  useEffect(() => {
    if (!considerSelected) return;

    const VALID_ASSET_IDS = [0, 1, 2, 3, 4, 5];

    // Utility: check if value is valid (0 or not null/undefined)
    const isValid = (value) => value === 0 || Boolean(value);

    // Utility: Fire dispatch + enable generate block
    const trigger = (dispatch, actionType, payload) => {
      dispatch({ type: actionType, payload });
      setRunGenerateComponentsBlock({ generateComponentsBlock: true });
    };

    const {
      selectedAsset,
      selectedAssetCB,
      selectedAssetPM,
      selectedLoad,
      selectedLoadCB,
      selectedLoadPM,
      selectedEv,
      selectedEvPM,
      selectedEvCB,
      selectedPv,
      selectedGt,
      selectedGtCB
    } = selectedValues || {};

    // --- ASSET CONFIG ---
    if (VALID_ASSET_IDS.includes(selectedAsset)) {
      trigger(CBAssetListDispatch, ACTIONS.UPDATE_ASSET_MODIFIED, {
        selectedAsset,
        configData: configData.configData,
      });
    }

    if (VALID_ASSET_IDS.includes(selectedAssetCB)) {
      trigger(CBAssetListDispatch, ACTIONS.UPDATE_HANDLE_CB_CONFIG, {
        selectedAssetCB,
        cbConfigData: configData.cbConfigData,
      });
    }

    if (VALID_ASSET_IDS.includes(selectedAssetPM)) {
      trigger(CBAssetListDispatch, ACTIONS.UPDATE_HANDLE_POWERMETER_CONFIG, {
        selectedAssetPM,
        cbConfigData: configData.cbConfigData,
      });
    }

    // --- LOAD CONFIG ---
    if (isValid(selectedLoad)) {
      trigger(CBLoadListDispatch, ACTIONS.UPDATE_HANDLE_LOAD_CONFIG, {
        selectedLoad,
        configData: configData.configData,
      });
    }

    if (isValid(selectedLoadCB)) {
      trigger(CBLoadListDispatch, ACTIONS.UPDATE_HANDLE_LOAD_CB_CONFIG, {
        selectedLoadCB,
        cbConfigData: configData.cbConfigData,
      });
    }

    if (isValid(selectedLoadPM)) {
      trigger(CBLoadListDispatch, ACTIONS.UPDATE_LOAD_HANDLE_POWERMETER_CONFIG, {
        selectedAssetPM: selectedLoadPM,
        cbConfigData: configData.cbConfigData,
      });
    }

    // --- EV CONFIG ---
    if (isValid(selectedEv)) {
      trigger(CBEVListDispatch, ACTIONS.UPDATE_HANDLE_EV_CONFIG, {
        selectedEv,
        configData: configData.configData,
      });
    }

    if (isValid(selectedEvPM)) {
      trigger(CBEVListDispatch, ACTIONS.UPDATE_HANDLE_EV_PM, {
        selectedAssetPM: selectedEvPM,
        cbConfigData: configData.cbConfigData,
      });
    }

    if (isValid(selectedEvCB)) {
      trigger(CBEVListDispatch, ACTIONS.UPDATE_HANDLE_EV_CB_CONFIG, {
        selectedEvCB,
        cbConfigData: configData.cbConfigData,
      });
    }

    // --- PV CONFIG ---
    if (isValid(selectedPv)) {
      setRunGenerateComponentsBlock({ generateComponentsBlock: true });
    }
    // --- GT CONFIG ---
    if (isValid(selectedGt)) {
      trigger(CBGTListDispatch, ACTIONS.UPDATE_HANDLE_GT_CONFIG, {
        selectedGt,
        configData: configData.gtConfigData,
      });
    }
    if (isValid(selectedGtCB)) {
      trigger(CBGTListDispatch, ACTIONS.UPDATE_HANDLE_GT_CB_CONFIG, {
        selectedGtCB,
        cbConfigData: configData.cbConfigData,
      });
    }

    sessionStorage.setItem("componentDataModified", true);

  }, [isAssetConfigChanged]);
  useEffect(() => {
    if (deploymentReady !== "true") {
      setCurrentProject((prev) => ({
        ...prev,
        showUseCase: false,
        showSystems: false,
        showBOM: false,
        showDeploy: false,
      }));
    }
  }, [deploymentReady]);
  useEffect(() => {
    if (document.getElementsByClassName("stickyProgressBar")?.[1]) {
      document.getElementsByClassName("stickyProgressBar")[1].style.display = "none";
    }
    if (document.getElementsByClassName('sticky-button')?.[1]) {
      document.getElementsByClassName('sticky-button')[1].style.display = "none";

    }

  }, [document.getElementsByClassName("stickyProgressBar")[1], document.getElementsByClassName('sticky-button')?.[1]]);

  useEffect(() => {

    if (isGridConnected) {
      if (isMgaasAnsi) {

        CBAssetListDispatch({
          type: ACTIONS.UPDATE_CBASSETLIST_COORDINATES_PVBESS_NR,
          payload: {
            switchType: switchboardType,
            mode: "",
            clientType: "",
            loadSide: projectKind?.isLoadSide ? "LOAD_SIDE" : "LINE_SIDE",
            projectKind: projectKind
          }
        });
        CBEVListDispatch({
          type: ACTIONS.UPDATE_CBEVLIST_COORDINATES_GRIDCONNECTED,
          payload: { coordinates: coordinates },
        });

      } else {
        CBAssetListDispatch({
          type: ACTIONS.UPDATE_CBASSETLIST_COORDINATES_GRIDCONNECTED,
          payload: { coordinates: coordinates },
        });
        CBLoadListDispatch({
          type: ACTIONS.UPDATE_CBLOADLIST_COORDINATES_GRIDCONNECTED,
        });
        CBEVListDispatch({
          type: ACTIONS.UPDATE_CBEVLIST_COORDINATES_GRIDCONNECTED,
          payload: { coordinates: coordinates },
        });
      }
    } else if (isIslandable &&
      isMgaasAnsi &&
      isNoFuel) {
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_CBASSETLIST_COORDINATES_PVBESS_NR,
        payload: {
          switchType: switchboardType,
          mode: defaultPreFillData[1]?.mode,
          clientType: "GENERIC",
          projectKind: projectKind
        },
      });
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_CBASSETLIST_COORDINATES_PVGENSET_ATS_SETTING_MODE,
        payload: {
          switchType: switchboardType,
          mode: defaultPreFillData[1]?.mode,
          clientType: "GENERIC",
          loadSide: loadSide,
        },
      });
    } else {
      CBEVListDispatch({
        type: ACTIONS.UPDATE_CBEVLIST_COORDINATES_GRIDCONNECTED,
        payload: { coordinates: coordinates },
      });
    }

  }, [coordinates]);

  useEffect(() => {
    sessionStorage.setItem("externalId", null);

  }, []);
  useEffect(() => {
    dispatch(getTooltipData(null));
  }, []);


  useEffect(() => {
    sessionStorage.removeItem("componentJsonData");
    if (projectId && projectTypeValue) {
      fetchDefaultDataValues();
      setDisplayPage(true);
      getExternalID();

    } else {
      // setAlertMessage(
      //   `Asset Page`,
      //   true,
      //   `information`,
      //   `Please create project and then visit asset page.`
      // );
    }
    sessionStorage.setItem("componentDataModified", false);
    sessionStorage.setItem(
      "derStatus",
      JSON.stringify([
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
        false,
      ])
    );
    sessionStorage.setItem(
      "derHistory",
      JSON.stringify({
        Utility: {},
        Genset: {},
        BESS: {},
        EV: {},
        CHP: {},
        Hybrid: {},
        GT: {},
      })
    );
    sessionStorage.setItem(
      "pvHistory",
      JSON.stringify({ pv1: [], pv2: [], pv3: [] })
    );
    sessionStorage.setItem("cbEvHistory", []);
    sessionStorage.setItem("cbLoadHistory", []);
    sessionStorage.setItem("cbAssetHistory", []);
    sessionStorage.setItem("pmAssetHistory", []);
    sessionStorage.setItem("pmEvHistory", []);
    sessionStorage.setItem("loadHistory", []);
    sessionStorage.setItem("cbGtHistory", []);
    sessionStorage.setItem("assetEditStatus", "");
    sessionStorage.setItem(
      "pvGroupPower",
      JSON.stringify({ pv1: "", pv2: "", pv3: "" })
    );
  }, [projectTypeValue]);

  //* To calculate coordinates

  useMountEffect(() => {
    if (initialRenderCoordinates.current) {
      initialRenderCoordinates.current = false;
    } else {
      calculateCoOridinates(runCalculateCoordinates.componentPush);
    }
  }, [runCalculateCoordinates, switchboardType]);

  useMountEffect(() => {
    if (runGenerateComponentsBlock.generateComponentsBlock) {
      generateComponentsBlock();
    }
  }, [runGenerateComponentsBlock]);

  useEffect(() => {
    if (initialRenderPvConfigured.current) {
      initialRenderPvConfigured.current = false;
    } else {
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_PV_CONFIRGURED,
        payload: { PVDetails },
      });
    }
  }, [PVDetails]);

  useMountEffect(() => {
    if (availableDer?.length) {
      checkUseCaseChangeByUser();
    }
  }, [availableDer]);
  useEffect(() => {
    getProjectDetailStatus();
    loadToolTip();
  }, [])
  //* functions
  const callSnackbar = useCallback((type, message) => {
    try {
      setSnackBar({ type, message });
      snackBarRef.current.open = true
    } catch (error) {
      console.log("error in snack bar")
    }
  }, []);

  const setAlertMessage = (title, isRoute, routePath, message) => {
    setAlert({
      title,
      isRoute,
      routePath,
      message,
      alertPopUpStatus: true,
    });
  };

  const closeAlertMessage = useCallback(() => {
    setAlert({
      message: "",
      alertPopUpStatus: false,
      title: "",
      isRoute: false,
      routePath: "",
    });
    if (alert.isRoute) {
      let { history } = props;
      history.push({
        pathname: `/${alert.routePath}`,
      });
    }
  }, [alert, props]);

  const setConfirmMessage = (message) => {
    setConfirm({
      confirmPopupStatus: true,
      confirmMessage: message,
    });
  };

  const closeConfirmMessage = () => {
    setConfirm({
      confirmPopupStatus: false,
      confirmMessage: "",
    });
  };
  const closeConfirmMessageRemote = () => {
    setRemoteConfirm({
      confirmPopupStatus: false,
      confirmMessage: "",
    });
  }
  const okConfirmMessageRemote = () => {
    if ((["MGaaS ANSI", "DaaS ANSI", "DaaS ETO"].includes(projectTypeValue) && [embLabels.ISLANDABLE, embLabels.OFF_GRID, embLabels.ISLANDABLE_GSX].includes(siteType)) && switchboardType === embLabels.EXISTING_SWITCHBOARD) {
      handleSwitchboardTypeChangeGSX("NEW_SWITCHBOARD");
    }
    closeConfirmMessageRemote();
  }

  const okConfirmMessage = (e, isConfirmed) => {
    if (PVDetails?.selectedPv === 3) {
      if (PVDetails?.pvset1Data.length > 1) {
        let pvConStatusOne = true;

        PVDetails?.pvset1Data?.forEach((pv1, index) => {
          if (index !== selectedValues?.selectSubPv - 1 && !pv1.status) {
            pvConStatusOne = false;
          }
        });

        CBAssetListDispatch({
          type: ACTIONS.REDUCE_PV_COUNT_FROM_CB,
          payload: {
            selectedPv: 3,
            pvConStatusOne: pvConStatusOne,
          },
        });

        PVDetailsDispatch({
          type: ACTIONS.REMOVE_PV_FROM_SET,
          payload: {
            selectedPv: selectedValues?.selectedPv,
            subPv: selectedValues?.selectSubPv,
          },
        });

        if (!PVDetails?.pvset1Data?.[0]?.assetDetails?.length) {
          const newAssetDetailsDisplay = { ...assetDetailsDisplay };
          Object.keys(newAssetDetailsDisplay).forEach(
            (details) => (newAssetDetailsDisplay[details] = "")
          );
          setAssetDetailsDisplay(newAssetDetailsDisplay);
        }

        //this.calculatePvStatusConfigured(newPvData1Set);

        let subPv = selectedValues?.selectSubPv - 1;

        updatePvHistory(subPv, 3);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);

        pv1SelectHandler();
      } else {
        setAlertMessage(
          `Alert`,
          false,
          ``,
          "Unable to delete. Minimum one Pv should be is present in PV set"
        );
      }
    }
    if (PVDetails?.selectedPv === 4) {
      if (PVDetails?.pvset2Data?.length > 1) {
        let pvConStatusOne = true;

        PVDetails?.pvset2Data?.forEach((pv2, index) => {
          if (index !== selectedValues?.selectSubPv - 1 && !pv2?.status) {
            pvConStatusOne = false;
          }
        });

        CBAssetListDispatch({
          type: ACTIONS.REDUCE_PV_COUNT_FROM_CB,
          payload: {
            selectedPv: 4,
            pvConStatusOne: pvConStatusOne,
          },
        });

        PVDetailsDispatch({
          type: ACTIONS.REMOVE_PV_FROM_SET,
          payload: {
            selectedPv: selectedValues?.selectedPv,
            subPv: selectedValues?.selectSubPv,
          },
        });

        if (!PVDetails?.pvset2Data?.[0]?.assetDetails?.length) {
          const newAssetDetailsDisplay = { ...assetDetailsDisplay };
          Object.keys(newAssetDetailsDisplay).forEach(
            (details) => (newAssetDetailsDisplay[details] = "")
          );

          setAssetDetailsDisplay(newAssetDetailsDisplay);
        }

        //this.calculatePvStatusConfigured(newPvData1Set);

        let subPv = selectedValues?.selectSubPv - 1;

        updatePvHistory(subPv, 4);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);

        pv2SelectHandler();
      } else {
        setAlertMessage(
          `Alert`,
          false,
          ``,
          "Unable to delete. Minimum one Pv should be is present in PV set"
        );
      }
    }
    if (PVDetails?.selectedPv === 5) {
      if (PVDetails?.pvset3Data?.length > 1) {
        let pvConStatusOne = true;

        PVDetails?.pvset3Data.forEach((pv3, index) => {
          if (index !== selectedValues?.selectSubPv - 1 && !pv3?.status) {
            pvConStatusOne = false;
          }
        });

        CBAssetListDispatch({
          type: ACTIONS.REDUCE_PV_COUNT_FROM_CB,
          payload: {
            selectedPv: 5,
            pvConStatusOne: pvConStatusOne,
          },
        });

        PVDetailsDispatch({
          type: ACTIONS.REMOVE_PV_FROM_SET,
          payload: {
            selectedPv: selectedValues?.selectedPv,
            subPv: selectedValues?.selectSubPv,
          },
        });

        if (!PVDetails?.pvset3Data?.[0]?.assetDetails.length) {
          const newAssetDetailsDisplay = { ...assetDetailsDisplay };
          Object.keys(newAssetDetailsDisplay).forEach(
            (details) => (newAssetDetailsDisplay[details] = "")
          );

          setAssetDetailsDisplay(newAssetDetailsDisplay);
        }

        //this.calculatePvStatusConfigured(newPvData1Set);

        let subPv = selectedValues?.selectSubPv - 1;

        updatePvHistory(subPv, 5);

        const runGenerateComponentsBlockNew = { generateComponentsBlock: true };
        setRunGenerateComponentsBlock(runGenerateComponentsBlockNew);

        pv3SelectHandler();
      } else {
        setAlertMessage(
          `Alert`,
          false,
          ``,
          "Unable to delete. Minimum one Pv should be is present in PV set"
        );
      }
    }
    closeConfirmMessage();
  };

  const FindNode = () => document.getElementById("SLD-Image");

  useImperativeHandle(ref, () => ({
    FindNode,
  }));

  const getCorespondingPlateCapacity = (obj, key) => {
    const objNew = JSON.parse(obj);
    for (const objKey in objNew) {
      if (objKey === key) {
        return objNew[objKey];
      }
    }
    return null;
  };
  // Function to filter based on hybrid model
  const filterHybridModel = (data, model) => {
    const storageCapacityKey = `${model}_storageCapacity`;
    const timeCapacityKey = `${model}_timeCapacity`;

    const storageCapacity = JSON.parse(data.find(item => item.type === storageCapacityKey)?.value) || [];
    const timeCapacity = JSON.parse(data.find(item => item.type === timeCapacityKey)?.value) || [];

    return {
      storageCapacity,
      timeCapacity
    };
  }
  const handleChangeBattery = (e) => {
    const { name, value } = e.target;

    if (name === "selectBatterySystemModel") {
      const { storageCapacity, timeCapacity } = filterHybridModel(defaultComponentData?.defaultComponentData?.assetAttributes, value);
      setBatterySystemA2EConfig(prev => {
        setConfigData((prevState) => ({
          ...prevState,
          configData: {
            ...prevState.configData,
            // hybridModels:hybridModels,
            namePlateEnergy: storageCapacity,
            time_capacity: timeCapacity
          },
        }));
        return {
          ...prev,
          namePlateEnergy: storageCapacity,
          namePlateCapacity: timeCapacity,

        }
      }
      );
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_BATTERY_CONFIG,
        payload: {
          index: 7,
          name: name,
          value: value,
          storageCapacity: storageCapacity,
          timeCapacity: timeCapacity,
        },
      });
    }
    if (name === "usableStorageCapacity") {
      CBAssetListDispatch({
        type: ACTIONS.UPDATE_BATTERY_CONFIG,
        payload: {
          index: 7,
          name: name,
          value: value,
          storageCapacity: "",
          timeCapacity: "",
          hybrid: defaultComponentData?.defaultComponentData?.hybrid ? defaultComponentData?.defaultComponentData?.hybrid : defaultComponentData?.defaultComponentData,
        },
      });
    }
    setIsAssetConfigChanged(configData);
  };
  const handleChangeBessHybridBatteryRack = (e, index) => {
    setBatterySystemA2EConfig(prev => {
      return {
        ...prev,
        namePlateEnergy: e.target.value,
      }
    }
    );
    const plateCapacityValue = getCorespondingPlateCapacity(
      configData?.configData?.corresponding_name_plate_capacity,
      e.target.value
    );
    CBAssetListDispatch({
      type: ACTIONS.UPDATE_BATTERY_CAPACITY_ON_BATTERY_RACK,
      payload: {
        index: 2,
        storage_capacity_hr_value: e.target.value,
        capacity: plateCapacityValue,
      },
    });
    let configDataNew = { ...configData?.configData };
    configDataNew["storage_capacity_hr_value"] = e.target.value;
    configDataNew["capacity"] = plateCapacityValue;
    setConfigData((prevState) => ({ ...prevState, configData: configDataNew }));
  };

  //* Wire up fnRef so hooks can call each other's functions
  fnRef.current = {
    callSnackbar, setAlertMessage, closeAlertMessage,
    generateComponentsBlock, calculateCoOridinates, checkAssetStatus,
    saveDraftComponentData, updateComponentRemote, getCBDetails, assignComponents,
    upDateCbcount, checkGensetDeleteOneDER, addGenset, addBattery, addPv,
    checkSLDCount, removeGenset, checkForDuplicateName, filterHybridModel,
    selectedValuesHandler, selectedSubPvHandler, assetDetailsReset, selectedAssetHandlerPv,
    updateDerHistory, updateDerStatus, updatePvHistory, updatePvHistoryGroupPower,
    updateLoadHistory, updateAssetCbHistory, updateLoadCbHistory,
    updateAssetPmHistory, updateEvPmHistory,
    assetEditStatusUpdateDER, assetEditStatusUpdateLoad,
    gtCbConfigDataHandler,setConfirmMessage,updatePvHistoryFull,updatePvHistoryGroupPower
  };

  return (
    <MyErrorBoundary>
      <div className={`container-fluid h-100 container-margin`}>
        <se-loading loading={loading}></se-loading>

        <div
          className={`${loading ? "se-block-disabled" : ""} row h-100 mt-2`}
          style={displayPage ? {} : { display: "none" }}
        >
          <AddEquipment
            spanishCodeRealTime={spanishCodeRealTime}
            loading={loading}
            siteType={siteType}
            clientType={clientType}
            switchboardType={switchboardType}
            handleSwitchboardTypeChangeGSX={handleSwitchboardTypeChangeGSX}
            remoteMain={remoteMain}
            setRemoteMain={setRemoteMain}
            handleRemoteMain={handleRemoteMain}
            architectureType={architectureType}
            onAddGensetVerify={addGensetVerify}
            onAddBattery={addBattery}
            onAddPv={addPv}
            onAddCHPVerify={addCHPVerify}
            onAddLoad={addLoad}
            onAddRemoteLoad={addRemoteLoad}
            onAddEv={addEv}
            onAddGroundingTransformer={addGroundingTransformer}
            onAddTransferSwitch={addTransferSwitch}
            gridSettingMode={defaultPreFillData[1]?.mode}
            projectStatus={projectStatusData?.projectStatus}
            siteCreated={siteCreated}
            count={count}
            PVDetails={PVDetails}
            availableDer={availableDer}
            useCases={useCases}
            handleSetLoadSide={handleSetLoadSide}
            loadSide={loadSide}
            onAddPvExisiting={onAddPvExisiting}
            onAddBatteryExisting={onAddBatteryExisting}
            allowSwitchBoard={
              CBAssetList[2].uiData.existingPmRequired ||
              CBAssetList[3].uiData.existingPmRequired
            }
            CBAssetList={CBAssetList}
            projectKind={projectKind}
            CBEVList={CBEVList}
            setRemoteConfirm={setRemoteConfirm}
            okConfirmMessageRemote={okConfirmMessageRemote}
            closeConfirmMessage={closeConfirmMessage}
            remoteConfirm={remoteConfirm}
            closeConfirmMessageRemote={closeConfirmMessageRemote}
            isAllowMultipleLoads={isAllowMultipleLoads}
            isExportOptimization={isExportOptimization}
            allowedCbCount={allowedCbCount}
            allowedLoadCount = {allowedLoadCount}
            gridTransitionOnline={gridTransitionOnline}
            checkSLDCount={checkSLDCount}
          />
          <div className="col-sm-8 col-md-8 col-lg-8 custom-project-container background-SLD ">
            <svg

              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 0
              }}

              width="100vw" height="100vh" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="tinySquares" x="0" y="0" width="2" height="2" patternUnits="userSpaceOnUse">
                  <rect x="0" y="0" width="3" height="3" fill="var(--se-svgline-bg)" stroke="var(--se-svg-background)" strokeWidth="0.1" />
                </pattern>
              </defs>
              <rect x="0" y="0" width="100" height="100" fill="url(#tinySquares)" />
            </svg>

            <DesignAssetDescription
              siteCreated={siteCreated}
              projectStatusData={projectStatusData}
              checkAssetStatus={checkAssetStatus}
              saveDraftComponentData={saveDraftComponentData}
              setIsOpen={setIsOpen}
            />
            <div className="row mt-5">
              <DesignAssetProvider
                value={{
                  clientType,
                  coordinates,
                  CBAssetList,
                  CBEVList,
                  CBLoadList,
                  CBGTList,
                  PVDetails,
                  UserScope,
                  sldLoadCompleted,
                  count,
                  siteType,
                  switchboardType,
                  architectureType,
                  rectangleWidth,
                  pv1SelectHandler,
                  pv2SelectHandler,
                  pv3SelectHandler,
                  CBAssetListDispatch,
                  CBLoadListDispatch,
                  CBEVListDispatch,
                  CBGTListDispatch,
                  PVDetailsDispatch,
                  assetDetailsReset,
                  callSnackbar,
                  selectedValuesHandler,
                  selectedSubPvHandler,
                  selectedValues,
                  selectedAssetHandler,
                  selectedAssetHandlerPv,
                  assetConfigDataHandler,
                  cbConfigDataHandler,
                  newSBCbConfigDataHandler,
                  loadConfigDataHandler,
                  loadCbConfigDataHandler,
                  gtCbConfigDataHandler,
                  pmConfigDataHandler,
                  existingPmConfigDataHandler,
                  loadpmConfigDataHandler,
                  evpmConfigDataHandler,
                  evConfigDataHandler,
                  evCbConfigDataHandler,
                  gtConfigDataHandler,
                  padNumberFormat,
                  detailsBlock,
                  loadSide,
                  projectTypeValue,
                  remoteMain,
                  projectKind,
                  isAntiIslandingRelayEnable,
                  loadSelectHandler,
                  gtSelectHandler,
                  isAllowMultipleLoads,
                  isExportOptimization,
                }}
              >
                {clientType === embLabels.A2E_PROJECT &&
                  siteType === embLabels.GENSET_PVBESS_A2E && (
                    <SldContainer siteType={siteType}>
                      <A2ESLD />
                    </SldContainer>
                  )}
                {/* This calls mgaas iec islandable */}
                {clientType === embLabels.GENERIC_PROJECT &&
                  siteType === embLabels.ISLANDABLE &&
                  !["MGaaS ANSI", "MGaaS ETO", "NMGaaS ETO"].includes(projectTypeValue) && (
                    <SldContainer siteType={siteType}>
                      <IslandableSLD />
                    </SldContainer>
                  )}
                {/* This calls mgaas iec grid connected */}
                {clientType === embLabels.GENERIC_PROJECT &&
                  siteType === embLabels.GRID_CONNECTED &&
                  !["MGaaS ANSI", "MGaaS ETO", "NMGaaS ETO"].includes(projectTypeValue) && (
                    <SldContainer siteType={siteType}>
                      <GridConnectedRosette />
                    </SldContainer>
                  )}

                {isMGaaSETOGrid && (
                  <SldContainer siteType={siteType}>
                    <GridEtoSLD />
                  </SldContainer>
                )}
                {isNMGaaSETOGrid && (
                  <SldContainer siteType={siteType}>
                    <GridEmaEtoSLD />
                  </SldContainer>
                )}
                {isNMGaaSETOIsland && (
                  <SldContainer siteType={siteType}>
                    <EmaIslandableEtoSLD />
                  </SldContainer>
                )}
                {isMGaaSETOIsland && (projectTypeValue === "MGaaS ETO") && (
                  <SldContainer siteType={siteType}>
                    <IslandableEtoSLD />
                  </SldContainer>
                )}
                {siteType === embLabels.OFF_GRID && (
                  <SldContainer siteType={siteType}>
                    <OffGridSLD />
                  </SldContainer>
                )}
                {clientType === embLabels.GENERIC_PROJECT &&
                  siteType === embLabels.PV_HYBRIDIZATION && (
                    // <PVHybridizationSLD />
                    <SldContainer siteType={siteType}>
                      <GensetPvHybridEto />
                    </SldContainer>
                  )}
                {/* for daas it never calls , IES never calls, Mgaas no call*/}
                {clientType === embLabels.GREENSTRUXURE_PROJECT &&
                  siteType === embLabels.ISLANDABLE_GSX &&
                  architectureType === embLabels.PV_GENSET_ATS &&
                  (defaultPreFillData[1].mode === "GENSET_MAIN_NO_BESS" ||
                    defaultPreFillData[1].mode === "NO_FUEL") && (
                    <SldContainer siteType={siteType}>
                      <PvGensetAtsSLD />
                    </SldContainer>
                  )}
                {/* daas ansi grid connected / mgaas ansi islandable */}
                {((clientType === embLabels.GREENSTRUXURE_PROJECT &&
                  siteType === embLabels.ISLANDABLE_GSX &&
                  architectureType === embLabels.PV_GENSET_ATS &&
                  defaultPreFillData[1].mode === "FUEL_SAVER") ||
                  (clientType === embLabels.GENERIC_PROJECT &&
                    siteType === embLabels.ISLANDABLE &&
                    projectTypeValue === "MGaaS ANSI")) && <GensetPvBessSLD />}
                {/* Daas Ansi/ mgaas ansi gridconnected || Daas ansi Islandable*/}
                {((clientType === embLabels.GREENSTRUXURE_PROJECT &&
                  siteType === embLabels.GRID_CONNECTED_GSX &&
                  architectureType === embLabels.PV_BESS_NR) ||
                  (clientType === embLabels.GENERIC_PROJECT &&
                    siteType === embLabels.GRID_CONNECTED &&
                    projectTypeValue === "MGaaS ANSI")) && (
                    <SldContainer siteType={siteType}>
                      <PvBessNrSLD />
                    </SldContainer>
                  )}
              </DesignAssetProvider>
            </div>
          </div>

          {/* configuration*/}
          <div
            className={`col-sm-2 col-md-2 col-lg-2 custom-configuration-container border se-white-background ${(siteCreated && projectTypeValue !== "NMGaaS ETO" )||
                projectStatusData?.projectStatus === "COMPLETED" ||
                projectStatusData?.projectStatus === "COMPLETED_AND_SEND"
                ? "se-block-disabled"
                : ""
              } `}
              style={{zIndex:114}}
          >
            <div className="row">
              <div className="col-md-12 pl-0 pr-0  se-white-background ">
                <div className="col-md-12 mt-1 p-2 se-life-green bold border-bottom">
                  CONFIGURATION
                </div>

              </div>
              <AssetBlockContextProvider
                value={{
                  spanishCodeRealTime,
                  assetDetailsReset,
                  UserScope,
                  callSnackbar,
                  CBLoadListDispatch,
                  selectedValuesHandler,
                  loadConfigDataHandler,
                  loadCbConfigDataHandler,
                  gtCbConfigDataHandler,
                  switchboardType,
                  siteType,
                  loadSide,
                  clientType,
                  configData,
                  defaultsMinMax,
                  assetDetailsDisplay,
                  projectTypeValue,
                  projectKind,
                  architectureType,
                  selectedValues,
                  PVDetails,
                  gsxExternalId,
                  CBAssetList,
                  CBEVList,
                  CBLoadList,
                  CBGTList,
                  batterySystemA2EConfig,
                  jsonData,
                  isContactorEnabled,
                  CBAssetListDispatch,
                  CBEVListDispatch,
                  CBGTListDispatch,
                  PVDetailsDispatch,
                  handleUtilityChange,
                  handleGensetChange,
                  handleBatteryChange,
                  handleChpChange,
                  handleHybridChange,
                  handleLoadChange,
                  handleEvChange,
                  handleCBConfigChange,
                  handleCBContactorConfigChange,
                  handleCBConfigChangeLoad,
                  handleCBConfigChangeEv,
                  updateUtilitytConfig,
                  updateGensetConfig,
                  updateBatteryConfig,
                  updateChpConfig,
                  updateHybridConfig,
                  updatePvArrayConfig,
                  updateLoadConfig,
                  updateEvConfig,
                  updatePowerMeterData,
                  updateCbConfigData,
                  updateCbContactorConfigData,
                  updateLoadCbConfigData,
                  updateEvCbConfigData,
                  setAssetSelection,
                  selectedAssetHandlerPv,
                  assetEditStatusUpdateDER,
                  assetEditStatusUpdateAssetCB,
                  assetEditStatusUpdateLoad,
                  assetEditStatusUpdateLoadCb,
                  assetEditStatusUpdateEvCb,
                  assetEditStatusUpdateAssetPM,
                  assetEditStatusUpdateEvPM,
                  assetEditStatusUpdatePv,
                  checkGensetRemoveCondition,
                  checkBatteryRemoveCondition,
                  removeCHP,
                  removePvWithAlert,
                  removePv,
                  removeLoad,
                  removeEv,
                  removeGt,
                  isCopyAllButtonDisabled,
                  copyAllPvs,
                  arrayMaxPowerFinder,
                  calculateMaxAvailableUtilityPower,
                  calculateMaxAvailableLoadPower,
                  calculateMaxAvailableGensetPower,
                  handleChangeBattery,
                  handleChangeBessHybridBatteryRack,
                  gridSettingMode: defaultPreFillData[1]?.mode,
                  removeExistingPm,
                  defaultCbData,
                  handleCbAssetRequired,
                  handleEnergyReserve,
                  handlePmAssetRequired,
                  handleEmAssetRequired,
                  handleBessController,
                  isBessController,
                  projectType,
                  handleLoadCBAssetRequired,
                  handleLoadPmAssetRequired,
                  checkAssetStatus,
                  setIsOpenLoadConfig,
                  selectedLoadIndx,
                  detailsBlock,
                  updateRemoteLoadCbConfigData,
                  isEvce,
                  setPrevPvGroupNames,
                  count,
                  callSnackbar,
                  checkSLDCount,
                  handleEVCbChange,
                  setIsOpenGtConfig,
                  gtConfigDataHandler
                }}
              >
                {detailsBlock.assetUtilityDetailsBlock && <UtilityAsset />}
                {detailsBlock.assetGensetDetailsBlock &&
                  [embLabels.GRID_CONNECTED, embLabels.ISLANDABLE, embLabels.OFF_GRID].includes(
                    siteType
                  ) && <GensetAsset />}
                {detailsBlock.assetGensetDetailsBlock &&
                  [embLabels.PV_HYBRIDIZATION].includes(siteType) && (
                    <GensetAsset />
                  )}
                {detailsBlock.assetGensetDetailsBlock &&
                  (([embLabels.ISLANDABLE_GSX].includes(siteType) &&
                    architectureType === embLabels.PV_GENSET_ATS) ||
                    ([embLabels.GENSET_PVBESS_A2E].includes(siteType) &&
                      clientType === embLabels.A2E_PROJECT)) && (
                    <GensetAssetPvHyb />
                  )}
                {detailsBlock.assetBatteryDetailsBlock && <BatteryAsset />}
                {detailsBlock.assetPvDetailsBlock && <PVAsset />}
                {detailsBlock.assetCHPDetailsBlock && <CHPAsset />}
                {detailsBlock.assetHybridDetailsBlock && <HybridAsset />}
                {detailsBlock.assetBessHybridDetailsBlock && (
                  <BessHybridAsset />
                )}
                {/* {detailsBlock.loadDetailsBlock && <LoadAsset />} */}
                {detailsBlock.loadDetailsBlock && <LoadAssetDetails cbCount={cbCount} />}
                {detailsBlock.cbLoadDetailsBlock && <RemoteLoadCbDetails />}
                {detailsBlock.evDetailsBlock && <EvCluster />}
                {detailsBlock.gtDetailsBlock && <GtDetails />}
                {detailsBlock.cbEvDetailsBlock && (
                  <EvCB detailsBlock={detailsBlock} />
                )}
                {detailsBlock.cbAssetDetailsBlock && (
                  <CircuitBreakerDetails
                    siteType={siteType}
                    architectureType={architectureType}
                    selectedValues={selectedValues}
                    projectKind = {projectKind}
                  />
                )}
                {detailsBlock.newSBCbAssetDetailsBlock && <GsxNewSBCbDetails />}
                {(detailsBlock.cbAssetDetailsBlockA2E ||
                  detailsBlock.pmAssetDetailsBlockA2E) && (
                    <CbPmDetailsA2E detailsBlock={detailsBlock} />
                  )}
                {(detailsBlock.cbAssetDetailsBlockGrid ||
                  detailsBlock.pmAssetDetailsBlockGrid) && (
                    <CbPmDetailsGrid detailsBlock={detailsBlock} />
                  )}
                {detailsBlock.existingPmAssetDetailsBlockGrid && (
                  <ExistingPmDetailsGrid detailsBlock={detailsBlock} />
                )}
                {detailsBlock.hardWiredCbDetailsBlockGrid && (
                  <HardWiredSBCbDetails />
                )}
                {isNoAssetSelected() && <NoAssetSelected />}
              </AssetBlockContextProvider>
            </div>
          </div>
        </div>

        <Snackbar snackBar={snackBar} ref={snackBarRef} />
        <AlertIcon alert={alert} onClose={closeAlertMessage} />

        <Confirm
          confirm={confirm}
          onClose={closeConfirmMessage}
          onOk={okConfirmMessage}
        />

        <AssetSelectionDER
          assetInfo={assetInfo}
          cbDer={cbDer}
          onSelectManufacturer={selectManufacturer}
          closeAssetSelectionBlock={closeAssetSelectionBlock}
          onSelectAssetInModal={selectAssetFromList}
          CBLoadList={CBLoadList}
          defaultsMinMax={defaultsMinMax}
          projectKind={projectKind}
          switchboardType={switchboardType}
          remoteMain={remoteMain}
          selectedValues={selectedValues}
          isSelectSpanishCode={isSelectSpanishCode}
          siteType={siteType}
        />

        <AssetSelectionPV
          siteType={siteType}
          architectureType={architectureType}
          assetDetailsDisplay={assetDetailsDisplay}
          PVDetails={PVDetails}
          gsxExternalId={gsxExternalId}
          onHandlePvChange={handlePvChange}
          onsetAssetSelection={setAssetSelection}
          assetEditStatusUpdatePv={assetEditStatusUpdatePv}
          defaultsMinMax={defaultsMinMax}
          onClosePvDetailsBlock={closePvDetailsBlock}
          onUpdatePvConfig={updatePvConfig}
          projectKind={projectKind}
        />
        <LoadConfigDetailsModal
          isOpenLoadConfig={isOpenLoadConfig}
          setIsOpenLoadConfig={setIsOpenLoadConfig}
          configData={configData}
          isLoadNameUnique={isLoadNameUnique}
          handleLoadChange={handleLoadChange}
          updateLoadConfig={updateLoadConfig}
          removeLoad={removeLoad}
          calculateMaxAvailableLoadPower={calculateMaxAvailableLoadPower}
          projectKind={projectKind}
          CBLoadList={CBLoadList}
          selectedValues={selectedValues}
          handleLoadCBAssetRequired={handleLoadCBAssetRequired}
          handleLoadPmAssetRequired={handleLoadPmAssetRequired}
          siteType={siteType}
          updateLoadCbConfigData={updateLoadCbConfigData}
          handleCBConfigChangeLoad={handleCBConfigChangeLoad}
          assetEditStatusUpdateLoadCb={assetEditStatusUpdateLoadCb}
          setAssetSelection={setAssetSelection}
          assetDetailsDisplay={assetDetailsDisplay}
          updateLoadCbPm={updateLoadCbPm}
          CBAssetList={CBAssetList}
          CBEVList={CBEVList}
          assetInfo={assetInfo}
          handleCBContactorConfigChangeLoad={handleCBContactorConfigChangeLoad}
        />
        <GtConfigDetailsModal
          isOpenGtConfig={isOpenGtConfig}
          setIsOpenGtConfig={setIsOpenGtConfig}
          setAssetSelection={setAssetSelection}
          configData={configData}
          CBGTList={CBGTList}
          selectedValues={selectedValues}
          assetDetailsDisplay={assetDetailsDisplay}
          gtCbConfigChangeHandler={handleGtCbConfigChange}
          gtChangeHandler={handleGtChange}
          updateGtCb={updateGtCb}
        />
        <AddPvModals
          spanishCodeRealTime={spanishCodeRealTime}
          CBAssetList={CBAssetList}
          siteType={siteType}
          clientType={clientType}
          architectureType={architectureType}
          PVDetails={PVDetails}
          onPvDetailsDispatch={PVDetailsDispatch}
          onAddPVSetWithCB={addPVSetWithCB}
          gridSettingMode={defaultPreFillData[1]?.mode}
          projectKind={projectKind}
        />
        <DraggableModal isOpen={isOpen} onClose={() => setIsOpen(prev => !prev)} projectKind={projectKind} assetDetails={assetDetails} />
      </div>
    </MyErrorBoundary>
  );
});

export default DesignAsset;
